import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  ChartBarIcon,
  CpuChipIcon,
  DocumentMagnifyingGlassIcon,
  WrenchScrewdriverIcon,
  ClipboardDocumentCheckIcon,
  ArrowRightIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

/**
 * Public landing page for CISO Advisor.
 *
 * Lives at "/" (the root). All sign-up paths route to /lead-capture
 * because every signup is a sales-assisted lead. The Sign In button
 * routes to /login for existing customers.
 */
export default function LandingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* ── Top Nav ────────────────────────────────────────────────── */}
      <nav className="border-b border-gray-700 bg-gray-900/60 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-primary-600 p-2 rounded-lg">
              <ShieldCheckIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold">CISO Advisor</h1>
              <p className="text-xs text-gray-400">by Moyer Scientific Research</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Link
              to="/login"
              className="text-sm text-gray-300 hover:text-white px-3 py-2 transition-colors"
            >
              Sign In
            </Link>
            <Link
              to="/lead-capture"
              className="text-sm bg-primary-600 hover:bg-primary-700 text-white font-semibold px-4 py-2 rounded-lg transition-colors"
            >
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* ── Hero ───────────────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <div className="inline-block px-4 py-1 bg-primary-600/10 border border-primary-600/30 rounded-full text-xs text-primary-300 font-semibold mb-6">
          Now Available · Beta Pricing for First 10 Customers
        </div>
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary-300 to-primary-500 bg-clip-text text-transparent leading-tight">
          A Full-Time CISO,<br />Without the Six-Figure Salary
        </h2>
        <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
          CISO Advisor is an agentic AI platform that delivers continuous security
          posture assessment, quantitative risk analysis, and prioritized
          remediation guidance — at a fraction of the cost of a traditional CISO.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/lead-capture"
            className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors inline-flex items-center justify-center space-x-2"
          >
            <span>Request a Demo</span>
            <ArrowRightIcon className="h-5 w-5" />
          </Link>
          <a
            href="#pricing"
            className="bg-gray-800 hover:bg-gray-700 border border-gray-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors inline-flex items-center justify-center"
          >
            See Pricing
          </a>
        </div>
      </section>

      {/* ── Feature Grid ───────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h3 className="text-3xl font-bold mb-3">What CISO Advisor Does</h3>
          <p className="text-gray-400 max-w-2xl mx-auto">
            A coordinated team of specialized AI agents working together — the
            same way a real security organization would.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Feature
            icon={ChartBarIcon}
            title="Quantitative Risk Analysis"
            description="Network modeling and Markov Chain Monte Carlo methods calculate the relative probability of successful attacks by vector — so you can prioritize objectively."
          />
          <Feature
            icon={ClipboardDocumentCheckIcon}
            title="Compliance Gap Analysis"
            description="Continuous gap analysis against PCI-DSS, NIST CSF, ISO 27001, HIPAA, SOC 2, and more. Know exactly where you stand."
          />
          <Feature
            icon={DocumentMagnifyingGlassIcon}
            title="Continuous Posture Assessment"
            description="Automated collectors pull live data from AWS, Azure, GCP, Qualys, CrowdStrike, and Splunk. Your security picture stays current."
          />
          <Feature
            icon={WrenchScrewdriverIcon}
            title="Prioritized Remediation"
            description="Not just a list of findings — a punch list ranked by risk reduction per dollar spent. You always know what to fix next."
          />
          <Feature
            icon={CpuChipIcon}
            title="Agentic Architecture"
            description="A master orchestrator routes your questions to specialist agents — compliance, threat intel, vulnerability management, incident response, and more."
          />
          <Feature
            icon={ShieldCheckIcon}
            title="Executive-Ready Reports"
            description="Board-quality reports generated on demand. Translate technical risk into business language your leadership team can act on."
          />
        </div>
      </section>

      {/* ── Pricing ────────────────────────────────────────────────── */}
      <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h3 className="text-3xl font-bold mb-3">Pricing Tiers</h3>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Beta pricing for the first 10 customers — 25% off standard rates.
            All tiers include the full agentic AI advisor; differences are in
            scope, frequency, and support.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <PricingCard
            name="Essential"
            tagline="Building security foundations"
            price="$27,000"
            cadence="per year (beta)"
            features={[
              'Single cloud environment',
              'Up to 50 network nodes',
              '2 compliance frameworks',
              'Quarterly executive reports',
              '3 user seats',
              'Email support (24–48hr)',
            ]}
          />
          <PricingCard
            name="Professional"
            tagline="Comprehensive security program"
            price="$48,750"
            cadence="per year (beta)"
            recommended
            features={[
              'Multi-cloud (AWS / Azure / GCP)',
              'Up to 250 network nodes',
              'All major compliance frameworks',
              'Monthly executive reports',
              '10 user seats',
              'Priority support (12–24hr)',
              'Monthly strategy calls',
              'Advanced analytics & reporting',
            ]}
          />
          <PricingCard
            name="Enterprise"
            tagline="White-glove security leadership"
            price="$82,500"
            cadence="per year (beta)"
            features={[
              'Unlimited cloud resources',
              'Unlimited network nodes',
              'All frameworks + custom',
              'Weekly executive reports',
              'Unlimited user seats',
              '24/7 support (4hr response)',
              'Weekly strategy calls',
              'Dedicated customer success manager',
              '10 consulting hours / month',
            ]}
          />
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/tier-selection"
            className="inline-flex items-center space-x-2 text-primary-400 hover:text-primary-300 font-semibold"
          >
            <span>Compare tiers in detail</span>
            <ArrowRightIcon className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* ── CTA Section ────────────────────────────────────────────── */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-gradient-to-r from-primary-700 to-primary-900 rounded-2xl p-10 text-center border border-primary-600/30">
          <h3 className="text-3xl font-bold mb-3">Ready to see it in action?</h3>
          <p className="text-primary-100 mb-8 max-w-xl mx-auto">
            Tell us a little about your organization and we'll set up a personalized
            walkthrough. No pressure, no hard sell.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/lead-capture"
              className="bg-white hover:bg-gray-100 text-primary-700 font-semibold px-8 py-4 rounded-lg transition-colors"
            >
              Request a Demo
            </Link>
            <Link
              to="/tier-selection"
              className="bg-primary-600 hover:bg-primary-500 border border-primary-400 text-white font-semibold px-8 py-4 rounded-lg transition-colors"
            >
              Browse Tiers
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────── */}
      <footer className="border-t border-gray-700 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-gray-400">
            © {new Date().getFullYear()} Moyer Scientific Research LLC. All rights reserved.
          </p>
          <div className="flex space-x-6 text-sm">
            <Link to="/login" className="text-gray-400 hover:text-white">
              Sign In
            </Link>
            <Link to="/lead-capture" className="text-gray-400 hover:text-white">
              Get Started
            </Link>
            <a
              href="https://moyerscientific.com"
              className="text-gray-400 hover:text-white"
            >
              Moyer Scientific
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

function Feature({ icon: Icon, title, description }) {
  return (
    <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-6 hover:border-primary-600/50 transition-colors">
      <div className="bg-primary-600/10 border border-primary-600/30 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
        <Icon className="h-6 w-6 text-primary-400" />
      </div>
      <h4 className="text-lg font-semibold mb-2">{title}</h4>
      <p className="text-sm text-gray-400 leading-relaxed">{description}</p>
    </div>
  );
}

function PricingCard({ name, tagline, price, cadence, features, recommended }) {
  return (
    <div
      className={`relative bg-gray-800/60 border rounded-xl p-6 ${
        recommended
          ? 'border-primary-500 shadow-lg shadow-primary-600/10'
          : 'border-gray-700'
      }`}
    >
      {recommended && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-500 text-white text-xs font-bold px-3 py-1 rounded-full">
          MOST POPULAR
        </div>
      )}
      <h4 className="text-xl font-bold mb-1">{name}</h4>
      <p className="text-sm text-gray-400 mb-4">{tagline}</p>
      <div className="mb-6">
        <span className="text-3xl font-bold">{price}</span>
        <span className="text-sm text-gray-400 ml-2">{cadence}</span>
      </div>
      <ul className="space-y-3 mb-6">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start space-x-2 text-sm">
            <CheckIcon className="h-5 w-5 text-primary-400 flex-shrink-0 mt-0.5" />
            <span className="text-gray-300">{feature}</span>
          </li>
        ))}
      </ul>
      <Link
        to="/lead-capture"
        className={`block text-center font-semibold py-3 px-4 rounded-lg transition-colors ${
          recommended
            ? 'bg-primary-600 hover:bg-primary-700 text-white'
            : 'bg-gray-700 hover:bg-gray-600 text-white'
        }`}
      >
        Get Started
      </Link>
    </div>
  );
}
